import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { formatTimeRemaining } from '@/utils/rateLimiting'
import { sanitizeEmail } from '@/utils/inputSanitization'

interface AuthContextType {
  user: User | null
  session: Session | null
  signUp: (email: string, password: string) => Promise<{ error: any }>
  signIn: (email: string, password: string) => Promise<{ error: any }>
  signOut: () => Promise<void>
  resendConfirmation: (email: string) => Promise<{ error: any }>
  loading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Set up auth state listener FIRST with error handling
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      try {
        console.log('Auth state changed:', event, session?.user?.email)
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
      } catch (error) {
        console.error('Error handling auth state change:', error)
        setLoading(false)
      }
    })

    // THEN check for existing session with error handling
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      try {
        if (error) {
          console.error('Error getting session:', error)
        }
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
      } catch (error) {
        console.error('Error processing session:', error)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // Session timeout: Auto-logout after 30 minutes of inactivity
  useEffect(() => {
    if (!user) return

    let timeoutId: NodeJS.Timeout
    let warningTimeoutId: NodeJS.Timeout

    const resetTimeout = () => {
      clearTimeout(timeoutId)
      clearTimeout(warningTimeoutId)

      // Warning at 25 minutes (5 min before logout)
      warningTimeoutId = setTimeout(
        () => {
          if (
            confirm(
              'Your session will expire in 5 minutes due to inactivity. Click OK to stay logged in.',
            )
          ) {
            resetTimeout() // Reset if user wants to stay
          }
        },
        25 * 60 * 1000,
      )

      // Auto-logout at 30 minutes
      timeoutId = setTimeout(
        () => {
          signOut()
          alert('You have been logged out due to inactivity.')
        },
        30 * 60 * 1000,
      )
    }

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']

    // Start timeout
    resetTimeout()

    // Reset timeout on user activity
    events.forEach((event) => {
      document.addEventListener(event, resetTimeout, true)
    })

    return () => {
      clearTimeout(timeoutId)
      clearTimeout(warningTimeoutId)
      events.forEach((event) => {
        document.removeEventListener(event, resetTimeout, true)
      })
    }
  }, [user])

  const signUp = async (email: string, password: string) => {
    const sanitizedEmail = sanitizeEmail(email)
    const redirectUrl = `https://indiescenejobs.com/profile`

    try {
      const { error } = await supabase.auth.signUp({
        email: sanitizedEmail,
        password,
        options: {
          emailRedirectTo: redirectUrl,
        },
      })
      return { error }
    } catch (error: any) {
      // Log authentication failure for security monitoring
      try {
        await supabase.rpc('log_auth_failure', {
          p_email: sanitizedEmail,
          p_ip_address: null,
          p_user_agent: navigator.userAgent,
          p_failure_reason: error.message || 'Sign up failed'
        });
      } catch (logError) {
        console.error('Failed to log auth failure:', logError);
      }
      
      return { error }
    }
  }

  const signIn = async (email: string, password: string) => {
    const sanitizedEmail = sanitizeEmail(email)
    const identifier = sanitizedEmail || 'unknown'

    try {
      // Check server-side rate limiting
      const { data: rateLimitData, error: rateLimitError } = await supabase.functions.invoke('rate-limit-check', {
        body: { identifier }
      })

      if (rateLimitError) {
        console.error('Rate limit check failed:', rateLimitError)
        // Continue with login attempt if rate limiting fails
      } else if (rateLimitData?.blocked) {
        const timeRemaining = rateLimitData.blockTimeRemaining || 0
        return {
          error: {
            message: `Too many login attempts. Please try again in ${formatTimeRemaining(timeRemaining)}.`,
          },
        }
      }

      const { error } = await supabase.auth.signInWithPassword({
        email: sanitizedEmail,
        password,
      })

      if (error) {
        // Record failed attempt
        await supabase.functions.invoke('rate-limit-check', {
          body: { identifier }
        })
        
        // Log authentication failure for security monitoring
        try {
          await supabase.rpc('log_auth_failure', {
            p_email: sanitizedEmail,
            p_ip_address: null,
            p_user_agent: navigator.userAgent,
            p_failure_reason: error.message || 'Sign in failed'
          });
        } catch (logError) {
          console.error('Failed to log auth failure:', logError);
        }
        
        throw error
      }

      // Record successful attempt
      await supabase.functions.invoke('rate-limit-check', {
        body: { identifier, action: 'success' }
      })
      
      return { error: null }
    } catch (error) {
      return { error }
    }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  const resendConfirmation = async (email: string) => {
    const sanitizedEmail = sanitizeEmail(email)
    
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: sanitizedEmail,
        options: {
          emailRedirectTo: `https://indiescenejobs.com/profile`,
        },
      })
      return { error }
    } catch (error) {
      return { error }
    }
  }

  const value = {
    user,
    session,
    signUp,
    signIn,
    signOut,
    resendConfirmation,
    loading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
